import { View, ViewOpts, animate } from '@play-co/timestep-core/ui';
import { DOWNSCALE_FACTOR } from 'src/conf/scale';
import { BuddiesMultiplierMark } from './BuddiesMultiplierMark';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { RadialShine } from 'src/game/components/shared/RadialShine';
import gsap from 'gsap';
import { SpineView, spineLoader } from '@play-co/timestep-spine';
import { playSpineAnimationSequence } from 'src/lib/ui/utils/spine';
import sounds from 'src/lib/sounds';
import { throttleLeading } from 'src/game/animation/animation.util';

export type BuddiesGemsRewardParams = {
  baseGems: number;
  totalGems: number;
  multiplier: number;
};

const spineAssets = {
  gems: {
    loadMethod: spineLoader,
    url: 'assets/ui/buddies/popupReward/gems/gems_rewards',
  },
};

export class BuddiesGemsReward extends View {
  public static getAssets = () => Object.values(spineAssets);

  private pileOfGems?: SpineView;
  private gemsAmount: LangBitmapFontTextView;
  private multiplier: BuddiesMultiplierMark;
  private shine: RadialShine;
  private params?: BuddiesGemsRewardParams;

  constructor(opts: ViewOpts) {
    super({
      ...opts,
    });

    this.shine = new RadialShine({
      superview: this,
      visible: false,
      centerOnOrigin: true,
      opacity: 0.2,
    });

    this.gemsAmount = new LangBitmapFontTextView({
      superview: this,
      align: 'center',
      verticalAlign: 'center',
      size: 80,
      font: 'Title',
      color: '#FFFFFF',
      width: 400,
      height: 200,
      centerOnOrigin: true,
      y: 100,
      localeText: () => 'x100',
      visible: false,
      letterSpacing: 4,
      zIndex: 1,
    });

    this.multiplier = new BuddiesMultiplierMark({
      superview: this,
      centerOnOrigin: true,
      visible: false,
      zIndex: 1,
    });
  }

  private async createPileOfGems() {
    if (this.pileOfGems) return;
    // Ensure spine is loaded
    await spineLoader(spineAssets.gems.url);
    this.pileOfGems = new SpineView({
      superview: this,
      spinePath: spineAssets.gems.url,
      x: this.style.width * 0.5,
      y: this.style.height * 0.5 + 130,
      scale: DOWNSCALE_FACTOR,
    });
  }

  public async setup(params: BuddiesGemsRewardParams) {
    this.params = params;
    this.gemsAmount.localeText = () => `x${params.baseGems}`;
    this.multiplier.setMultiplier(params.multiplier);
  }

  public async showAnimated() {
    if (!this.params) {
      console.error('BuddiesGemsReward: Missing params for animation');
      return;
    }

    const { baseGems, totalGems } = this.params;

    this.show();

    await this.createPileOfGems();

    // First present pile of gems and shine
    if (this.pileOfGems) {
      playSpineAnimationSequence(this.pileOfGems, {
        sequence: ['gems_init', 'gems_idle'],
        loopLast: true,
        clear: true,
      });
    }
    this.shine.updateOpts({ visible: true, scale: 0 });
    animate(this.shine).then({ scale: 2 }, 500);
    sounds.sfx.play('win');

    await animate(this).wait(300).toPromise();

    // Start with base gems counting up
    this.gemsAmount.updateOpts({ visible: true, scale: 0 });
    await animate(this.gemsAmount).then({ scale: 0.75 }, 300).toPromise();

    // Show the multiplier

    this.multiplier.updateOpts({ visible: true, scale: 0 });
    await animate(this.multiplier)
      .then({ scale: 1 }, 1000, animate.easeOutElastic)
      .toPromise();
    animate(this.multiplier)
      .then({ offsetY: -100, opacity: 0 }, 600, animate.easeInExpo)
      .toPromise();

    // If base and total are the same, skip the gems counting animation
    if (baseGems === totalGems) {
      return;
    }

    const duration = 1000;

    if (this.pileOfGems) {
      playSpineAnimationSequence(this.pileOfGems, {
        sequence: ['gems_more_init', 'gems_more_idle'],
        loopLast: true,
        clear: true,
      });
    }
    animate(this.shine).then({ scale: 3 }, duration, animate.linear);
    animate(this.gemsAmount).then({ scale: 1 }, duration, animate.linear);

    // Animate the gems amount from baseGems to totalGems
    const gems = {
      value: this.params.baseGems,
    };

    let lastScore = gems.value;
    await gsap.to(gems, {
      value: totalGems,
      duration: duration / 1000,
      ease: 'linear',
      onUpdate: () => {
        const newScore = Math.round(gems.value);
        if (newScore === lastScore) return;
        lastScore = newScore;
        this.playGemsSfx();
        this.gemsAmount.localeText = () => `x${newScore}`;
      },
    });

    this.gemsAmount.localeText = () => `x${totalGems}`;
    sounds.sfx.play('applause');
  }

  private playGemsSfx = throttleLeading(() => {
    // That's to avoid playing the sound too many times if the score updates too quickly
    sounds.sfx.play('gemsCollected');
  }, 100);
}
